import React from 'react';
import styled from '@emotion/styled';
import { Popper, Fade, useTheme } from '@mui/material';
import PolarisIcon from '@/components/_common/PolarisIcon';
import IconButton from '@/components/Packaging/common/IconButton';
import { SEGMENT_TOOLBAR_POPPER_ZINDEX } from '@/components/Packaging/constants';

interface Props {
  /**
   * `isInPopper` indicates whether the toolbar is rendered inside a Popper.
   * Happens when there is not enough space to render toolbar directly in the overlay (when overlay width is too small).
   */
  isInPopper: boolean;
  hasAssignedInventory: boolean;
  onDuplicateButtonClick?(): void;
  onAssignInventoryButtonClick?(): void;
  isLocked: boolean;
  onLockButtonClick(): void;
  color: string;
  onRemoveButtonClick(): void;
}

type IconAppearance = 'light' | 'dark';

const ToolbarComponent: React.FC<Props> = ({
  hasAssignedInventory,
  isInPopper,
  onDuplicateButtonClick,
  onAssignInventoryButtonClick,
  isLocked,
  onLockButtonClick,
  color,
  onRemoveButtonClick
}: Props) => {
  const theme = useTheme();

  const iconAppearance: IconAppearance = theme.palette.mode === 'light' ? 'dark' : hasAssignedInventory ? 'dark' : 'light';

  return (
    <ToolbarWrapper isInPopper={isInPopper} color={color} locked={isLocked} hasAssignedInventory={hasAssignedInventory}>
      {onDuplicateButtonClick && (
        <IconButton title="Duplicate" tooltipPlacement="top" onClick={onDuplicateButtonClick}>
          <PolarisIconWrapped icon="duplicate" height={16} appearance={iconAppearance} />
        </IconButton>
      )}
      {onAssignInventoryButtonClick && (
        <IconButton title="Assign inventory" tooltipPlacement="top" onClick={onAssignInventoryButtonClick}>
          <PolarisIconWrapped icon={hasAssignedInventory ? 'folder_outline' : 'folder_open_outline'} height={16} appearance={iconAppearance} />
        </IconButton>
      )}
      <IconButton title={isLocked ? 'Unlock' : 'Lock'} tooltipPlacement="top" onClick={onLockButtonClick}>
        <PolarisIconWrapped icon={isLocked ? 'lock_closed' : 'lock_open'} height={16} appearance={iconAppearance} />
      </IconButton>
      <StyledRemoveButton title={'Remove'} tooltipPlacement="top" onClick={onRemoveButtonClick}>
        <PolarisIconWrapped icon="close" height={18} appearance={iconAppearance} />
      </StyledRemoveButton>
    </ToolbarWrapper>
  );
};

interface ToolbarProps extends Props {
  isPopperOpen: boolean;
  segmentWrapperElement: HTMLElement | null;
}

const Toolbar: React.FC<ToolbarProps> = ({ isInPopper, isPopperOpen, segmentWrapperElement, ...restToolbarComponentProps }) => {
  if (isInPopper) {
    return (
      <Popper open={isPopperOpen} anchorEl={segmentWrapperElement} placement="top" transition sx={{ zIndex: SEGMENT_TOOLBAR_POPPER_ZINDEX }}>
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={300}>
            <div>
              <ToolbarComponent isInPopper={isInPopper} {...restToolbarComponentProps} />
            </div>
          </Fade>
        )}
      </Popper>
    );
  }

  return <ToolbarComponent isInPopper={isInPopper} {...restToolbarComponentProps} />;
};

export default Toolbar;

const ToolbarWrapper = styled.div<{
  isInPopper: boolean;
  color: string;
  locked: boolean;
  hasAssignedInventory: boolean;
}>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  column-gap: ${({ theme }) => theme.spacing(0.75)};
  padding: ${({ theme, isInPopper }) => theme.spacing(isInPopper ? 0.5 : 0.25, 1)};
  ${({ theme, isInPopper }) => (isInPopper ? `margin: ${theme.spacing(1)} 0;` : '')}
  border-radius: ${({ theme, isInPopper }) => {
    const borderRadius = theme.shape.borderRadius / 2;

    if (isInPopper) {
      return `${borderRadius}px;`;
    }

    return `0 0 ${borderRadius}px ${borderRadius}px;`;
  }};
  background: ${({ theme, color, hasAssignedInventory }) => {
    if (hasAssignedInventory) {
      return color;
    }

    return theme.palette.mode === 'dark' ? 'rgba(65, 70, 107, 0.8)' : 'rgba(202,206,217,0.8)';
  }};
`;

const PolarisIconWrapped = styled(PolarisIcon, {
  shouldForwardProp: (prop) => !['hasAssignedInventory'].includes(prop)
})<{ appearance: IconAppearance }>`
  display: block;
  color: ${({ appearance }) => {
    return appearance === 'dark' ? '#000000' : '#ffffff';
  }};
  transition: opacity 0.2s;
  opacity: 0.66;
  cursor: pointer;

  &:hover {
    opacity: 1;
  }
`;

const StyledRemoveButton = styled(IconButton)`
  cursor: pointer;
  opacity: 1;
`;
